import React, { useState, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatToPKR, displayDate } from "@/lib/helper";
import { Search, Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import Combobox from "@/components/ui/common/combobox";
import { FacilitySlot } from "@/types/facility/booking";
import { FacilityAddOnType, FacilityType } from "@/types/enums";
import { Facility } from "@/types/facility";
import { MemberTableDatatypes } from "@/types/member";
import { toast } from "@/components/ui/use-toast";
import {
  useGetAllFacilityScheduleSlotQuery,
  useCriteriaValidationQuery,
  useGetFacilitiesQuery,
  useLazyGetFacilityScheduleSlotDetailsQuery,
  useAddToCartFacilitySlotMutation,
} from "@/services/facilityApis";
import { useLazyGetPriceMastersQuery } from "@/services/priceMasterApi";
import { SlotSelector } from "@/components/ui/common/slot-selector";
import { format } from "date-fns";
import { calculateTaxFromTaxGroups } from "@/lib/tax-utils";
import { useGetSalesTaxListQuery, useLazyGetSalesTaxByIdQuery } from "@/services/salesTaxApi";
import { calculateItemPrice } from "@/lib/pricing-utils";
import { PriceMaster } from "@/types/system-setting/priceMaster";
import { useGetAllProductsQuery } from "@/services/productApi";
import { SlotDetailResponse } from "@/types/facility/schedule";

interface FacilitySlotsProps {
  orgId: number;
  selectedMember: MemberTableDatatypes | null;
  onAddToCart: (slotData: any) => void;
  cartedSlotIds?: number[];
  orgTaxType?: "inclusive" | "exclusive";
  selectedMemberTaxGroup?: any;
  taxGroups?: any[];
}

interface SelectedAddOn {
  id: number;
  name: string;
  item_type: string;
  price: number;
  type: string;
  quantity: number;
  min_limit?: number;
}

interface FacilitySlotCartItem {
  slot: FacilitySlot;
  schedule: any;
  selectedAddOns: SelectedAddOn[];
  totalPrice: number;
  subtotal: number;
  taxAmount: number;
  finalTotal: number;
  incomeCategoryId: number;
  taxRate: number;
  taxName: string;
  item_tax_group?: any;
  slotDetail?: SlotDetailResponse;
  facility?: Facility;
}

const getSlotKey = (slot: FacilitySlot) =>
  `${slot.slot_date}__${slot.start_time}__${slot.end_time}`;

const FacilitySlots: React.FC<FacilitySlotsProps> = ({
  orgId,
  selectedMember,
  onAddToCart,
  cartedSlotIds = [],
  orgTaxType = "exclusive",
  selectedMemberTaxGroup = null,
  taxGroups = [],
}) => {
  // ── State ──────────────────────────────────────────────────────────────
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<FacilitySlot | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>([]);
  const [slotOpen, setSlotOpen] = useState(false);

  // Resolved slot detail from the API (flat shape)
  const [slotDetail, setSlotDetail] = useState<SlotDetailResponse | null>(null);
  // Price master map: id → PriceMaster
  const [priceMap, setPriceMap] = useState<Record<number, PriceMaster>>({});
  // Resolved tax info
  const [resolvedTax, setResolvedTax] = useState<{
    taxRate: number;
    taxName: string;
    itemTaxGroup: any;
  } | null>(null);
  const [addonBlockedSlots, setAddonBlockedSlots] = useState<Record<string, string>>({});

  // ── API hooks ──────────────────────────────────────────────────────────
  const [getSalesTaxById] = useLazyGetSalesTaxByIdQuery();
  const [getSlotDetails] = useLazyGetFacilityScheduleSlotDetailsQuery();
  const [getPriceMasters] = useLazyGetPriceMastersQuery();
  const [addToCartFacilitySlot, { isLoading: isAddingToCart }] = useAddToCartFacilitySlotMutation();
  const { data: salesTaxList } = useGetSalesTaxListQuery(orgId, { skip: !orgId });
  const { data: productData } = useGetAllProductsQuery({
    query: "status=active&sort_key=id&sort_order=desc",
    org_id: orgId,
  }, { skip: !orgId });

  const { data: facilitiesData } = useGetFacilitiesQuery({
    orgId,
    query: "status=active&bookable=true&only_available_slots=true",
  });

  const startDate = format(currentMonth, "yyyy-MM-01");
  const endDate = format(
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0),
    "yyyy-MM-dd"
  );

  const { data: slotsData, isLoading: slotsLoading } = useGetAllFacilityScheduleSlotQuery(
    {
      orgId,
      query: `facility_id=${selectedFacility?.id}&type=facility_schedule&load_available=true&is_available=false&sort_order=desc`,
      startDate,
      endDate,
    },
    { skip: !selectedFacility?.id },
  );

  const { error: validationError } = useCriteriaValidationQuery(
    {
      org_id: orgId,
      member_id: selectedMember?.id || 0,
      facility_id: selectedFacility?.id || 0,
    },
    { skip: !selectedMember?.id || !selectedFacility?.id },
  );

  // ── Derived data ───────────────────────────────────────────────────────
  const facilities = useMemo(() => facilitiesData?.data || [], [facilitiesData]);

  const facilityOptions = useMemo(
    () =>
      facilities
        .filter((f) => f.id !== undefined)
        .map((f) => ({ label: f.name, value: f.id!.toString() })),
    [facilities],
  );

  // Available add-ons from the selected slot detail
  const availableAddOns = useMemo(() => {
    if (!slotDetail?.addons) return [];
    return slotDetail.addons.map((a) => {
      let isAvailable = a.is_available ?? true;
      let availableQuantity = a.available_quantity;

      if (a.item_type === "product" && a.item_id) {
        const prod = productData?.data?.find((p) => p.id === a.item_id);
        if (prod && prod.manage_inventory) {
          const qty = prod.quantity ?? 0;
          if (qty <= 0) {
             if (!selectedFacility?.require_approval) {
              isAvailable = false;
              availableQuantity = 0;
            } else {
              availableQuantity = qty; // Keep the actual quantity (even if 0) but don't mark as unavailable
            }
          } else {
            availableQuantity = qty;
          }
        }
      }

      return {
        id: a.id,
        name: a.name,
        price: a.price,
        type: a.type,
        item_type: a.item_type,
        item_id: a.item_id,
        max_attendees: a.max_attendees ?? 0,
        facility_type: a.facility_type,
        description: a.description,
        access_control: a.access_control,
        is_available: isAvailable,
        available_quantity: availableQuantity,
        price_master_id: a.price_master_id,
        min_limit: a.min_limit,
      };
    });
  }, [slotDetail, productData, selectedFacility]);

  // Check if any mandatory (inclusive) add-on is unavailable or out of stock
  const isMandatoryAddonUnavailable = useMemo(() => {
    if (selectedFacility?.require_approval) return false;
    return availableAddOns.some(
      (addon) =>
        addon.type === FacilityAddOnType.Inclusive &&
        (addon.is_available === false ||
          (addon.available_quantity !== undefined && addon.available_quantity <= 0))
    );
  }, [availableAddOns, selectedFacility]);

  // Trigger validation toast when a slot detail is loaded and mandatory addons are unavailable
  React.useEffect(() => {
    if (isMandatoryAddonUnavailable) {
      toast({
        variant: "destructive",
        title: "Validation failed",
        description: "Required add-on product is not available for this slot.",
      });
    }
  }, [isMandatoryAddonUnavailable]);

  // Basic slot list for the calendar selector (no pricing enrichment needed here)
  const availableSlots: FacilitySlot[] = useMemo(() => {
    if (!slotsData?.data) return [];
    const now = new Date();
    return slotsData.data.map((slot: any) => {
      let isAvailable = true;
      let reason = "";

      // Calculate approved bookings count
      const approvedCount = slot.consumed_slots?.filter((s: any) =>
        s.status?.toLowerCase() === "approved" ||
        s.status?.toLowerCase() === "confirmed" ||
        s.status?.toLowerCase() === "booking_approved"
      ).length || 0;

      // 1. Check capacity
       if (selectedFacility && selectedFacility.require_approval) {
        // If approval is required, only approved bookings consume capacity
        const maxCapacity = slot.max_attendees || (slot.remaining_capacity + (slot.consumed_slots?.length || 0)) || 0;
        if (maxCapacity > 0 && approvedCount >= maxCapacity) {
          isAvailable = false;
          reason = "Fully Booked";
        }
        // Also check facility-wide capacity if available
        if (selectedFacility.max_capacity && approvedCount >= selectedFacility.max_capacity) {
          isAvailable = false;
          reason = "Not Available";
        }
      } else {
        // Standard validation: pending bookings also consume capacity
        if (slot.remaining_capacity !== undefined && slot.remaining_capacity <= 0) {
          isAvailable = false;
          reason = "Fully Booked";
        }
      }

      // 2. Check if blocked (backend status)
      if (isAvailable && slot.status === "blocked") {
        isAvailable = false;
        reason = "Blocked";
      }
      
      // 3. Check if past
      if (isAvailable) {
        try {
          const slotDateTime = new Date(`${slot.slot_date}T${slot.start_time}`);
          if (slotDateTime < now) {
            isAvailable = false;
            reason = "Not Available";
          }
        } catch (e) {
          // ignore date errors
        }
      }

      // 4. Check if slot's is_available is false or 0 from backend
      if (isAvailable && (slot.is_available === false || slot.is_available === 0)) {
        isAvailable = false;
        reason = "Not Available";
      }

      const builtSlot: FacilitySlot = {
        id: slot.id,
        price: slot.price || 0,
        facility_schedule_id: slot.facility_schedule_id || 0,
        slot_date: format(new Date(slot.slot_date), "yyyy-MM-dd"),
        start_time: slot.start_time,
        end_time: slot.end_time,
        always_available: slot.always_available || false,
        duration_hours: slot.duration_hours,
        is_exclusive: slot.is_exclusive || false,
        total_attendees: slot.consumed_slots?.length || 0,
        status: slot.status || null,
        is_exclusive_schedule: slot.is_exclusive || false,
        is_available: isAvailable,
        unavailability_reason: reason,
      };
      const key = getSlotKey(builtSlot);
      if (addonBlockedSlots[key]) {
        builtSlot.is_available = false;
        builtSlot.unavailability_reason = "Not Available";
      }

      return builtSlot;
    }) as FacilitySlot[];
  }, [slotsData, selectedFacility, addonBlockedSlots]); // addonBlockedSlots in deps triggers re-render

  // ── Tax resolution ─────────────────────────────────────────────────────
  const resolveTax = useCallback(
    async (taxGroupId?: number) => {
      // We check for the tax_group object, but if it's missing (flat response),
      // we resolve it from the taxGroups list using tax_group_id
      const facilityTaxGroup =
        (slotDetail as any)?.tax_group ||
        (slotDetail?.tax_group_id && taxGroups?.find(tg => tg.id === slotDetail.tax_group_id));

      const memberTaxGroup = selectedMemberTaxGroup || (selectedMember?.tax_group && (Array.isArray(selectedMember.tax_group) ? selectedMember.tax_group[0] : selectedMember.tax_group));

      let { tax, taxRate, taxName } = calculateTaxFromTaxGroups(
        facilityTaxGroup,
        memberTaxGroup || null,
      );

      if (!tax && facilityTaxGroup?.default_tax_id && salesTaxList) {
        const defaultTax = (salesTaxList as any[]).find(
          (t) => t.id === facilityTaxGroup.default_tax_id
        );
        if (defaultTax) {
          setResolvedTax({
            taxRate: defaultTax.percentage,
            taxName: defaultTax.name,
            itemTaxGroup: facilityTaxGroup,
          });
          return;
        }

        try {
          const resp = await getSalesTaxById({
            sale_tax_id: facilityTaxGroup.default_tax_id,
            orgId,
          }).unwrap();
          if (resp) {
            setResolvedTax({
              taxRate: resp.percentage,
              taxName: resp.name,
              itemTaxGroup: facilityTaxGroup,
            });
            return;
          }
        } catch (e) {
          console.error(e);
        }
      }

      setResolvedTax({ taxRate, taxName, itemTaxGroup: facilityTaxGroup });
    },
    [selectedMember, getSalesTaxById, orgId, slotDetail, selectedMemberTaxGroup, taxGroups],
  );

  // Re-resolve tax when slot detail or member changes
  React.useEffect(() => {
    if (slotDetail) {
      resolveTax(slotDetail.tax_group_id);
    }
  }, [slotDetail, selectedMember, selectedMemberTaxGroup, taxGroups, resolveTax]);

  // ── Resolve addon price via price master rules ─────────────────────────
  const resolveAddonPrice = useCallback(
    (addon: { price: number; base_price?: number; price_master_id?: number }): { price: number; priceType: string; appliedPricingName: string } => {
      const addonPriceMaster =
        addon.price_master_id ? priceMap[addon.price_master_id] : null;

      // Base price: price master price → addon base_price → addon display price
      const baseRegularPrice =
        addonPriceMaster?.price ?? addon.base_price ?? addon.price ?? 0;

      if (!addonPriceMaster) return { price: baseRegularPrice, priceType: "base", appliedPricingName: "" };

      // Build pricing rule arrays from the addon's own price master
      const specialPricing =
        addonPriceMaster.special_pricing?.map((sp) => ({
          name: sp.name,
          price: sp.price,
          date_from: sp.date_from || "",
          date_to: sp.date_to || "",
        })) || [];

      const planPricing =
        addonPriceMaster.plan_pricing?.map((pp) => ({
          group_id: pp.group_id,
          membership_plan_ids: pp.membership_plan_ids,
          price: pp.price,
        })) || [];

      const groupPricing =
        addonPriceMaster.group_pricing?.map((gp) => ({
          group_id: gp.group_id,
          price: gp.price,
        })) || [];

      const result = calculateItemPrice(baseRegularPrice, {
        specialPricing,
        planPricing,
        groupPricing,
        member: selectedMember,
        referenceDate: slotDetail?.slot_date,
      });

      return result;
    },
    [priceMap, selectedMember, slotDetail],
  );


  React.useEffect(() => {
    if (!slotDetail?.addons) return;

    const inclusiveAddOns: SelectedAddOn[] = slotDetail.addons
      .filter(
        (a) =>
          a.type === FacilityAddOnType.Inclusive &&
          (a.is_available ?? true)
      )
      .map((a) => ({
        id: a.id,
        name: a.name,
        item_type: a.item_type,
        price: resolveAddonPrice(a).price,
        type: a.type,
        min_limit: a.min_limit,
        quantity:
          a.max_attendees && a.max_attendees > 0
            ? (selectedFacility?.require_approval 
                ? a.max_attendees 
                : Math.min(a.max_attendees, a.available_quantity ?? a.max_attendees))
            : 1,
      }));

    setSelectedAddOns((prev) => {
      const nonInclusive = prev.filter(
        (i) => i.type !== FacilityAddOnType.Inclusive
      );

      return [...nonInclusive, ...inclusiveAddOns];
    });
  }, [slotDetail, priceMap, resolveAddonPrice]);

  // ── Pricing calculation ────────────────────────────────────────────────
  const calculateSlotPricing = useCallback(() => {
    if (!slotDetail || !selectedSlot) return null;

    // Look up the slot's price master for pricing rules
    const slotPriceMaster = slotDetail.price_master_id
      ? priceMap[slotDetail.price_master_id]
      : null;

    // Base price: use price master price → slot base_price → slot price
    const baseRegularPrice =
      slotPriceMaster?.price ?? slotDetail.base_price ?? slotDetail.price ?? 0;

    // Build pricing rule arrays from price master
    const specialPricing = slotPriceMaster?.special_pricing?.map((sp) => ({
      name: sp.name,
      price: sp.price,
      date_from: sp.date_from || "",
      date_to: sp.date_to || "",
    })) || [];

    const planPricing = slotPriceMaster?.plan_pricing?.map((pp) => ({
      group_id: pp.group_id,
      membership_plan_ids: pp.membership_plan_ids,
      price: pp.price,
    })) || [];

    const groupPricing = slotPriceMaster?.group_pricing?.map((gp) => ({
      group_id: gp.group_id,
      price: gp.price,
    })) || [];

    console.log(`this is specialPricing ${JSON.stringify(specialPricing)}`)
    console.log(`this is planPricing ${JSON.stringify(planPricing)}`)
    console.log(`this is groupPricing ${JSON.stringify(groupPricing)}`)

    // Use shared utility for best-price selection
    const result = calculateItemPrice(baseRegularPrice, {
      specialPricing,
      planPricing,
      groupPricing,
      member: selectedMember,
      referenceDate: slotDetail.slot_date,
    });

    const basePrice = result.price;

    // Add-ons total – resolve each addon price via price master rules
    const addOnsTotal = selectedAddOns.reduce((total, addOn) => {
      const addonDetail = slotDetail.addons?.find((a) => a.id === addOn.id);
      const resolvedPrice = addonDetail
        ? resolveAddonPrice(addonDetail).price
        : addOn.price;
      return total + resolvedPrice * addOn.quantity;
    }, 0);

    const subtotal = basePrice + addOnsTotal;

    // Tax
    const taxRate = resolvedTax?.taxRate || 0;
    const taxName = resolvedTax?.taxName || "";
    let taxAmount = 0;
    if (taxRate > 0) {
      taxAmount =
        orgTaxType === "inclusive"
          ? subtotal - subtotal / (1 + taxRate / 100)
          : (subtotal * taxRate) / 100;
    }

    const finalTotal = orgTaxType === "inclusive" ? subtotal : subtotal + taxAmount;

    return {
      basePrice,
      addOnsTotal,
      subtotal,
      taxRate,
      taxAmount,
      taxName,
      finalTotal,
      priceType: result.priceType,
      appliedPricingName: result.priceType === "group" ? slotPriceMaster?.rule_name : result.appliedPricingName,
    };
  }, [slotDetail, selectedSlot, priceMap, selectedMember, selectedAddOns, resolvedTax, orgTaxType, resolveAddonPrice]);

  const pricing = calculateSlotPricing();

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleFacilitySelect = (facility: Facility) => {
    setSelectedFacility(facility);
    setSelectedSlot(null);
    setSelectedAddOns([]);
    setSlotDetail(null);
    setPriceMap({});
    setResolvedTax(null);
    setAddonBlockedSlots({}); // clear blocked slots when facility changes
  };

  const handleSlotSelect = async (slot: FacilitySlot) => {

    if (!selectedMember) {
      toast({
        variant: "destructive",
        title: "Member required",
        description: "Please select a member before selecting a facility slot",
      });
      return;
    }

    if (selectedSlot && getSlotKey(slot) === getSlotKey(selectedSlot)) {
      setSlotOpen(false);
      return;
    }

    if (cartedSlotIds.includes(slot.id)) {
      toast({
        variant: "destructive",
        title: "Already in cart",
        description: "This facility slot is already in your cart.",
      });
      return;
    }

    if (validationError) {
      const error = validationError as any;
      toast({
        variant: "destructive",
        title: "Validation failed",
        description:
          error?.data?.detail ||
          error?.data?.message ||
          "Member does not meet facility criteria",
      });
      return;
    }

    // Reset state before fetching new slot
    setSlotDetail(null);
    setPriceMap({});
    setResolvedTax(null);
    setSelectedAddOns([]);
    setSlotOpen(false);

    try {
      // 1. Fetch slot details (flat response)
      const detailsResponse = await getSlotDetails({
        org_id: orgId,
        member_id: selectedMember?.id,
        schedule_id: slot.facility_schedule_id,
        start_time: `${slot.slot_date}T${slot.start_time}`,
        end_time: `${slot.slot_date}T${slot.end_time}`,
        load_available: true,
        load_pricing: true
      }).unwrap();

      const details: SlotDetailResponse =
        (detailsResponse as any)?.data ?? detailsResponse;

      setSelectedSlot(slot);
      setSlotDetail(details);

      // 2. Collect all price_master_ids (slot + addons)
      const priceMasterIds = new Set<number>();
      if (details.price_master_id) {
        priceMasterIds.add(details.price_master_id);
      }
      details.addons?.forEach((addon) => {
        if (addon.price_master_id) {
          priceMasterIds.add(addon.price_master_id);
        }
      });
      // 3. Fetch price masters in a single call with load_pricing=true
      if (priceMasterIds.size > 0) {
        const idsParam = Array.from(priceMasterIds)
          .map((id) => `price_master_ids%5B%5D=${id}`)
          .join("&");
        const pmResponse = await getPriceMasters({
          org_id: orgId,
          query: `${idsParam}&load_pricing=true&sort_order=desc&sort_key=id`,
        }).unwrap();

        // 4. Build price map: id → PriceMaster
        const map: Record<number, PriceMaster> = {};
        (pmResponse?.data || []).forEach((pm: PriceMaster) => {
          if (pm.id) map[pm.id] = pm;
        });
        setPriceMap(map);
      } else {
        setPriceMap({});
      }
    } catch (err) {
      console.error("Error fetching slot details / price masters:", err);
    }
  };

  const handleAddOnToggle = (addOn: any, checked: boolean) => {
    if (addOn.type === FacilityAddOnType.Inclusive) return;

     if (!selectedFacility?.require_approval) {
      if (!addOn.is_available) {
        toast({
          variant: "destructive",
          title: "Addon not available",
          description: `${addOn.name} is currently not available.`,
        });
        return;
      }

    if (checked && addOn.available_quantity <= 0) {
        toast({
          variant: "destructive",
          title: "Addon out of stock",
          description: `${addOn.name} is currently out of stock.`,
        });
        return;
      }
    }

    if (checked) {
      // Resolve price through price master rules for the exclusive addon
      const addonDetail = slotDetail?.addons?.find((a: any) => a.id === addOn.id);
      const resolvedPrice = addonDetail
        ? resolveAddonPrice(addonDetail).price
        : addOn.price;

      setSelectedAddOns((prev) => [
        ...prev,
        {
          id: addOn.id,
          name: addOn.name,
          price: resolvedPrice,
          item_type: addOn.item_type,
          type: addOn.type,
          min_limit: addOn.min_limit,
          quantity: addOn.max_attendees && addOn.max_attendees > 0 ? addOn.max_attendees : 1,
        },
      ]);
    } else {
      setSelectedAddOns((prev) => prev.filter((item) => item.id !== addOn.id));
    }
  };

  const handleAddOnQuantityChange = (addOnId: number, quantity: number) => {
    const addOn = availableAddOns.find((a) => a.id === addOnId);

    if (quantity <= 0) {
      if (addOn?.type === FacilityAddOnType.Inclusive) {
        // Don't remove inclusive add-ons, keep at minimum 1
        return;
      }
      setSelectedAddOns((prev) => prev.filter((item) => item.id !== addOnId));
    } else {
      // Check stock limit only if approval is NOT required
      if (
        !selectedFacility?.require_approval &&
        addOn &&
        addOn.available_quantity !== undefined &&
        quantity > (addOn.available_quantity ?? 0)
      ) {
        toast({
          variant: "destructive",
          title: "Quantity limit exceeded",
          description: `Only ${addOn.available_quantity ?? 0} units of ${addOn.name} are available.`,
        });
        return;
      }

      setSelectedAddOns((prev) =>
        prev.map((item) => (item.id === addOnId ? { ...item, quantity } : item)),
      );
    }
  };

  const handleAddToCart = async () => {
    if (!selectedSlot || !slotDetail || !pricing) return;

    if (isMandatoryAddonUnavailable) {
      toast({
        variant: "destructive",
        title: "Validation failed",
        description: "Required add-on product is not available for this slot.",
      });
      return;
    }

    if (
      !(selectedFacility?.credit_only === true || selectedFacility?.facility_type === FacilityType.Trainer) &&
      pricing.finalTotal <= 0
    ) {
      toast({
        variant: "destructive",
        title: "Pricing not configured",
        description:
          "This facility's Price Master/Income Category isn't configured to match its current booking mode. Please reconfigure in Schedule.",
      });
      return;
    }

    try {
      const startIso = `${selectedSlot.slot_date}T${selectedSlot.start_time}`;
      const endIso = `${selectedSlot.slot_date}T${selectedSlot.end_time}`;

      const payload = {
        org_id: orgId,
        schedule_id: selectedSlot.facility_schedule_id,
        date: selectedSlot.slot_date,
        start_time: startIso,
        end_time: endIso,
        duration_hours: selectedSlot.duration_hours || 0,
        price_master_id: slotDetail.price_master_id || 0,
        max_attendees: slotDetail.max_attendees || 0,
        color: slotDetail.color || "",
        location: slotDetail.location || "",
        tax_group_id: slotDetail.tax_group_id || 0,
        income_category_id: slotDetail.income_category_id || 0,
        advance_booking_days: slotDetail.advance_booking_days || 0,
        member_id: selectedMember?.id || 0,
        addon_ids: selectedAddOns.map((a) => a.id),
      };

      const response = await addToCartFacilitySlot(payload).unwrap();

      if (response) {
        const cartItem: FacilitySlotCartItem = {
          slot: {
            ...selectedSlot,
            id: (response as any).id || selectedSlot.id,
          },
          schedule: {
            general_info: {
              id: slotDetail.facility_schedule_id,
              facility_id: slotDetail.facility_id,
              facility_name: slotDetail.facility_name || slotDetail.name || selectedFacility?.name,
              price: slotDetail.price,
              price_master_id: slotDetail.price_master_id,
              tax_group_id: slotDetail.tax_group_id,
              income_category_id: slotDetail.income_category_id,
              color: slotDetail.color,
              location: slotDetail.location,
              max_attendees: slotDetail.max_attendees,
              advance_booking_days: slotDetail.advance_booking_days,
            },
          },
          selectedAddOns: [...selectedAddOns],
          totalPrice: pricing.finalTotal,
          subtotal: pricing.subtotal,
          taxAmount: pricing.taxAmount,
          finalTotal: pricing.finalTotal,
          incomeCategoryId: slotDetail.income_category_id || 0,
          taxRate: pricing.taxRate,
          taxName: pricing.taxName,
          item_tax_group: resolvedTax?.itemTaxGroup,
          slotDetail,
          facility: selectedFacility || undefined,
        };

        onAddToCart({
          ...cartItem,
          facilityId: slotDetail.facility_id,
        });

        // Reset
        setSelectedFacility(null);
        setSelectedSlot(null);
        setSelectedAddOns([]);
        setSlotDetail(null);
        setPriceMap({});
        setResolvedTax(null);
        setAddonBlockedSlots({});

        toast({
          variant: "success",
          title: "Added to cart",
          description: "Facility slot has been added to your cart successfully.",
        });
      }
    } catch (error: any) {
      console.error("Error adding to cart:", error);
      toast({
        variant: "destructive",
        title: "Error adding to cart",
        description:
          error?.data?.detail ||
          error?.data?.message ||
          "Something went wrong while adding the slot to cart.",
      });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Card className="p-4 space-y-4">
      {/* Facility Selection */}
      <Combobox
        key={selectedFacility?.id || "empty"}
        list={facilityOptions}
        label="Select Facility"
        defaultValue={selectedFacility?.name || ""}
        setFilter={(selectedLabel: string) => {
          const facility = facilities.find((f) => f.name === selectedLabel);
          if (facility) handleFacilitySelect(facility);
        }}
        className="w-full"
        showLabel={false}
      />

      {/* Slot Selection */}
      {selectedFacility && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Select Slot</label>
            {!selectedMember && (
              <span className="text-[10px] text-amber-600 font-medium animate-pulse flex items-center gap-1">
                <Search className="h-2.5 w-2.5" />
                Select a member first
              </span>
            )}
          </div>
          <SlotSelector
            slots={availableSlots}
            selectedSlot={selectedSlot}
            onSlotSelect={handleSlotSelect}
            isLoading={slotsLoading}
            action="add"
            cartedSlotIds={cartedSlotIds}
            disabled={!selectedMember}
            onMonthChange={setCurrentMonth}
            key={`slot-selector-${selectedFacility?.id || "none"}`}
          />
        </div>
      )}

      {/* Add-ons Selection */}
      {selectedSlot && availableAddOns.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">Available Add-ons</h4>
          <div className="space-y-2">
            {availableAddOns.map((addOn) => {
              const selectedAddOn = selectedAddOns.find((i) => i.id === addOn.id);
              const isInclusive = addOn.type === FacilityAddOnType.Inclusive;
              const isAddonAvailable =
                addOn.is_available !== false &&
                (addOn.available_quantity === undefined || addOn.available_quantity > 0);

              return (
                <div
                  key={addOn.id}
                  className={cn(
                    "flex items-center justify-between p-2 border rounded",
                    isInclusive && "bg-blue-50/50 border-blue-100",
                  )}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!selectedAddOn || isInclusive}
                        onChange={(e) => handleAddOnToggle(addOn, e.target.checked)}
                        disabled={isInclusive || (!isAddonAvailable && !selectedFacility?.require_approval)}
                        className={cn(
                          "rounded",
                          (isInclusive || (!isAddonAvailable && !selectedFacility?.require_approval)) &&
                          "cursor-not-allowed opacity-50",
                        )}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <p
                            className={cn(
                              "text-sm font-medium",
                              !isAddonAvailable && "text-red-600",
                            )}
                          >
                            {addOn.name}
                          </p>
                          {isInclusive && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase">
                              Inclusive
                            </span>
                          )}
                          {!isAddonAvailable && (
                            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold uppercase">
                              Out of Stock
                            </span>
                          )}
                        </div>
                        <p
                          className={cn(
                            "text-xs text-gray-500",
                            !isAddonAvailable && "text-red-500",
                          )}
                        >
                          {addOn.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm">{formatToPKR(
                      (() => {
                        const detail = slotDetail?.addons?.find((a) => a.id === addOn.id);
                        return detail ? resolveAddonPrice(detail).price : addOn.price;
                      })()
                    )}</span>
                    {selectedAddOn && selectedAddOn.item_type === "product" && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleAddOnQuantityChange(addOn.id, selectedAddOn.quantity - 1)
                          }
                          disabled={isInclusive ? selectedAddOn.quantity <= 1 : selectedAddOn.quantity <= 0}
                          className="h-6 w-6 p-0"
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm w-8 text-center">
                          {selectedAddOn.quantity}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleAddOnQuantityChange(addOn.id, selectedAddOn.quantity + 1)
                          }
                          className="h-6 w-6 p-0"
                          disabled={
                            selectedAddOn.quantity >=
                            (selectedFacility?.require_approval
                              ? addOn.max_attendees || Infinity
                              : Math.min(
                                  addOn.available_quantity || Infinity,
                                  addOn.max_attendees || Infinity,
                                ))
                          }
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Slot Details & Pricing */}
      {selectedSlot && slotDetail && (
        <div className="space-y-4">
          {/* Slot Details */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Slot Details</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Facility:</span>
                <span>{slotDetail.facility_name || slotDetail.name || selectedFacility?.name}</span>
              </div>
              <div className="flex justify-between">
                <span>Date:</span>
                <span>{displayDate(selectedSlot.slot_date)}</span>
              </div>
              <div className="flex justify-between">
                <span>Time:</span>
                <span>
                  {selectedSlot.start_time} - {selectedSlot.end_time}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Duration:</span>
                <span>
                  {selectedSlot.duration_hours ??
                    (Number(selectedSlot.end_time.split(":")[0]) * 60 +
                      Number(selectedSlot.end_time.split(":")[1]) -
                      (Number(selectedSlot.start_time.split(":")[0]) * 60 +
                        Number(selectedSlot.start_time.split(":")[1]))) /
                    60}
                  h
                </span>
              </div>
            </div>
          </div>

          {/* Validation Warning Alert */}
          {isMandatoryAddonUnavailable && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-start gap-2 text-sm">
              <span className="font-semibold">Booking Blocked:</span>
              <span>Required add-on product is not available for this slot.</span>
            </div>
          )}

          {/* Pricing Breakdown */}
          {pricing && !(selectedFacility?.credit_only === true || selectedFacility?.facility_type === FacilityType.Trainer) && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Pricing Breakdown</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span>Base Price:</span>
                    {pricing.priceType === "special" && (
                      <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold uppercase">
                        Special: {pricing.appliedPricingName || "Pricing"}
                      </span>
                    )}
                    {pricing.priceType === "plan" && (
                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold uppercase">
                        Plan: {pricing.appliedPricingName || "Pricing"}
                      </span>
                    )}
                    {pricing.priceType === "group" && (
                      <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-bold uppercase">
                        Group: {pricing.appliedPricingName || "Pricing"}
                      </span>
                    )}
                  </div>
                  <span>{formatToPKR(pricing.basePrice)}</span>
                </div>
                {selectedAddOns.length > 0 && (
                  <div className="flex justify-between">
                    <span>Add-ons:</span>
                    <span>{formatToPKR(pricing.addOnsTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatToPKR(pricing.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax ({pricing.taxRate}%):</span>
                  <span>{formatToPKR(pricing.taxAmount)}</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-bold">
                  <span>Total:</span>
                  <span>{formatToPKR(pricing.finalTotal)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Add to Cart Button */}
          {pricing ? (
            <>
              <Button
                onClick={handleAddToCart}
                disabled={
                  !selectedSlot ||
                  !pricing ||
                  isAddingToCart ||
                  (
                    !(selectedFacility?.credit_only === true ||
                      selectedFacility?.facility_type === FacilityType.Trainer) &&
                    pricing.finalTotal <= 0
                  )
                }
                className={cn(
                  "w-full",
                  isMandatoryAddonUnavailable && "bg-red-600 hover:bg-red-700 text-white"
                )}
              >
                {isAddingToCart
                  ? "Adding..."
                  : isMandatoryAddonUnavailable
                    ? "Required Add-on Out of Stock"
                    : `Add to Cart (${formatToPKR(pricing?.finalTotal || 0)})`}
              </Button>
              {!(selectedFacility?.credit_only === true || selectedFacility?.facility_type === FacilityType.Trainer) &&
                pricing.finalTotal <= 0 && (
                  <p className="mt-2 text-sm text-red-600">
                    This facility's Price Master/Income Category isn't configured to match its current booking mode.
                    Please reconfigure in Schedule.
                  </p>
                )}
            </>
          ) : (
            <Button
              onClick={handleAddToCart}
              disabled={!selectedSlot || !pricing || isAddingToCart}
              className="w-full"
            >
              {isAddingToCart ? "Adding..." : "Add to Cart"}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default FacilitySlots;
